import { useEffect, useState, useCallback, useRef } from 'react';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { View } from 'foundations-components/components/View';
import { Text } from 'foundations-components/components/Text';
import { Link } from 'foundations-components/transitional/components/Link';
import { ScrollContainer } from 'foundations-components/transitional/components/ScrollContainer';
import { List, ListItem } from 'foundations-components/transitional/components/List';
import CompanionFooter from './CompanionFooter';
import InteractiveContentError from './InteractiveContentError';
import InteractiveEducationLoading from './InteractiveEducationLoading';
import UsageTracker from '../../../utils/UsageTracker';
import { useVideoTimestamp } from '../../video/videoHooks';
import { useCurrentVideoTime } from '../../video/components/VideoPlayerWithControls';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
// Determine navigation type based on current and previous chapter
const getNavigationType = (currentIndex, lastIndex) => {
  if (lastIndex === null) return 'initial';
  if (currentIndex === lastIndex) return 'same';
  return currentIndex > lastIndex ? 'forward' : 'backward';
};

// Format seconds to hh:mm:ss (or mm:ss when hours are 0)
const formatSecondsToTimestamp = totalSeconds => {
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor(totalSeconds % 3600 / 60);
  const s = Math.floor(totalSeconds % 60);
  return h ? `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}` : `${m}:${String(s).padStart(2, '0')}`;
};
const SummarizeTab = ({
  additionalTracking,
  primaryContentId,
  contentType,
  summary,
  correlationId,
  summaryError,
  summaryLoading,
  refetchSummary,
  videoDuration
}) => {
  var _summary$sections2;
  const [clickHistory, setClickHistory] = useState([]);
  const [userHasScrolled, setUserHasScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState(-1);
  const [hoveredSection, setHoveredSection] = useState(-1);

  // Ref to wrapper div for scroll event handling
  const scrollContainerRef = useRef(null);
  const sectionRefs = useRef([]);
  // Timeout for resuming auto-scroll after user interaction
  const autoScrollTimeoutRef = useRef(null);
  // Timestamp of last auto-scroll to distinguish from user scroll
  const lastAutoScrollTime = useRef(0);

  // Timing constants for scroll behavior
  const AUTO_SCROLL_DETECTION_DELAY = 500;
  const RESUME_AUTO_SCROLL_DELAY = 3000;
  const CHAPTER_CLICK_RESUME_DELAY = 2000;

  // Video player hooks for timestamp control and current state
  const {
    setVideoTimestamp
  } = useVideoTimestamp();
  const {
    currentVideoTime,
    hasVideoPlayed,
    isVideoFinished
  } = useCurrentVideoTime();

  // Reset click history when content changes
  useEffect(() => {
    setClickHistory([]);
  }, [primaryContentId]);

  // Find the current active section based on video timestamp
  // Default to first chapter (0) if video hasn't reached first timestamp yet
  const getCurrentSection = useCallback(t => {
    var _summary$sections;
    if (!(summary !== null && summary !== void 0 && (_summary$sections = summary.sections) !== null && _summary$sections !== void 0 && _summary$sections.length)) return -1;
    const index = summary.sections.findLastIndex(s => s.timestamp <= t);
    return index === -1 ? 0 : index;
  }, [summary === null || summary === void 0 ? void 0 : summary.sections]);

  // Auto-scroll to keep the active section visible and centered in the container
  const scrollToSection = useCallback(sectionIndex => {
    // Skip if invalid section or user is manually scrolling
    if (sectionIndex < 0 || !sectionRefs.current[sectionIndex] || !scrollContainerRef.current || userHasScrolled) return;
    const sectionElement = sectionRefs.current[sectionIndex];
    const scrollableElement = scrollContainerRef.current;
    if (sectionElement && scrollableElement && scrollableElement.scrollTo) {
      const containerHeight = scrollableElement.clientHeight;

      // Get current positions of section and container
      const sectionRect = sectionElement.getBoundingClientRect();
      const containerRect = scrollableElement.getBoundingClientRect();

      // Calculate where section should scroll to be centered
      const sectionHeight = sectionElement.clientHeight;
      const relativeTop = sectionRect.top - containerRect.top + scrollableElement.scrollTop;
      const targetScrollTop = relativeTop - containerHeight / 2 + sectionHeight / 2;

      // Scroll to center the section smoothly
      scrollableElement.scrollTo({
        top: Math.max(0, targetScrollTop),
        behavior: 'smooth'
      });

      // Track timing to distinguish auto-scroll from user scroll
      lastAutoScrollTime.current = Date.now();
    }
  }, [userHasScrolled]);

  // Handle scroll events to detect user interaction
  const handleScroll = useCallback(() => {
    const now = Date.now();
    // Only consider it user scroll if it happened after the detection delay
    if (now - lastAutoScrollTime.current > AUTO_SCROLL_DETECTION_DELAY) {
      setUserHasScrolled(true);

      // Clear existing timeout
      if (autoScrollTimeoutRef.current) clearTimeout(autoScrollTimeoutRef.current);

      // Resume auto-scroll after delay of no user interaction
      autoScrollTimeoutRef.current = setTimeout(() => {
        setUserHasScrolled(false);
      }, RESUME_AUTO_SCROLL_DELAY);
    }
  }, []);

  // Update active section based on video time and handle auto-scroll
  useEffect(() => {
    if (currentVideoTime >= 0 && summary !== null && summary !== void 0 && summary.sections) {
      const currentSection = getCurrentSection(currentVideoTime);
      if (currentSection !== activeSection) {
        setActiveSection(currentSection);
        scrollToSection(currentSection);
      }
    }
  }, [currentVideoTime, activeSection, getCurrentSection, scrollToSection, summary === null || summary === void 0 ? void 0 : summary.sections]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (autoScrollTimeoutRef.current) clearTimeout(autoScrollTimeoutRef.current);
    };
  }, []);

  /**
   * Handles chapter navigation clicks and tracks analytics
   * Captures accurate video state based on actual play/finish events
   * Also pauses auto-scroll temporarily when user manually navigates
   */
  const handleChapterClick = useCallback((chapterIndex, chapterTitle, chapterTimestamp) => {
    var _lastClick$chapterInd;
    const currentTime = Date.now();

    // Optimistically update active section for instant visual feedback
    setActiveSection(chapterIndex);

    // Pause auto-scroll when user manually clicks a chapter
    setUserHasScrolled(true);

    // Clear existing timeout
    if (autoScrollTimeoutRef.current) clearTimeout(autoScrollTimeoutRef.current);

    // Resume auto-scroll after chapter click delay
    autoScrollTimeoutRef.current = setTimeout(() => {
      setUserHasScrolled(false);
    }, CHAPTER_CLICK_RESUME_DELAY);
    // Calculate video progress as a percentage (0-1)
    const videoProgress = videoDuration && videoDuration > 0 && currentVideoTime >= 0 ? Math.min(currentVideoTime / videoDuration, 1) : 0;

    // Determine video playback state based on actual user interaction
    let videoPlaybackState;
    if (isVideoFinished) videoPlaybackState = 'finished';else if (hasVideoPlayed) videoPlaybackState = 'started';else videoPlaybackState = 'not_started';

    // Calculate derived fields from click history
    const clickCount = clickHistory.length + 1; // Total clicks in session
    const isRevisit = clickHistory.some(click => click.chapterIndex === chapterIndex // Has user visited this chapter before?
    );
    const lastClick = clickHistory[clickHistory.length - 1];
    const navigationType = getNavigationType(chapterIndex, (_lastClick$chapterInd = lastClick === null || lastClick === void 0 ? void 0 : lastClick.chapterIndex) !== null && _lastClick$chapterInd !== void 0 ? _lastClick$chapterInd : null // Compare with previous chapter
    );
    const timeOnChapter = lastClick ? currentTime - lastClick.timestamp : 0; // Time spent on previous chapter

    const trackingData = Object.assign({
      action: 'ai-summarize-chapter-clicked',
      indexPosition: chapterIndex,
      // Start from 0
      title: chapterTitle,
      videoTimestamp: chapterTimestamp,
      // Video timestamp in seconds where chapter starts
      progressLevel: videoProgress,
      // Current video progress as percentage (0-1)
      state: videoPlaybackState,
      // Video playback state
      // Derived fields for analytics
      existing: isRevisit,
      // Has user visited this chapter before?
      previousValue: navigationType,
      // Direction of navigation (initial/forward/backward/same)
      actionCount: clickCount,
      // Total clicks in current session
      pageVisitTime: timeOnChapter
    }, additionalTracking);

    // Update click history
    setClickHistory(prev => [...prev, {
      chapterIndex,
      timestamp: currentTime
    }]);
    UsageTracker.trackStandalone('Learning Center Interaction', trackingData);

    // Navigate video to the selected chapter timestamp
    setVideoTimestamp(chapterTimestamp);
  }, [currentVideoTime, additionalTracking, setVideoTimestamp, videoDuration, hasVideoPlayed, isVideoFinished, clickHistory]);
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(View, {
      backgroundColor: summaryError ? 'fill-surface-recessed' : undefined,
      paddingBlockStart: "700",
      paddingInline: "700",
      children: summaryError ? /*#__PURE__*/_jsx(InteractiveContentError, {
        handleRetry: () => void refetchSummary(),
        title: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "learningCenter.interactiveEducation.summarize.summarizeTitle"
        })
      }) : summaryLoading ? /*#__PURE__*/_jsx(InteractiveEducationLoading, {
        carouselTexts: [/*#__PURE__*/_jsx(FormattedMessage, {
          message: "learningCenter.interactiveEducation.loading.summarize.fact1"
        }, "fact1"), /*#__PURE__*/_jsx(FormattedMessage, {
          message: "learningCenter.interactiveEducation.loading.summarize.fact2"
        }, "fact2"), /*#__PURE__*/_jsx(FormattedMessage, {
          message: "learningCenter.interactiveEducation.loading.summarize.fact3"
        }, "fact3"), /*#__PURE__*/_jsx(FormattedMessage, {
          message: "learningCenter.interactiveEducation.loading.summarize.fact4"
        }, "fact4")],
        mainText: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "learningCenter.interactiveEducation.loading.buildingSummary"
        })
      }) : /*#__PURE__*/_jsx("div", {
        onScroll: handleScroll,
        children: /*#__PURE__*/_jsxs(ScrollContainer, {
          blockSize: "450px",
          maxHeight: 450,
          paddingBlockEnd: "700",
          scrollElementRef: scrollContainerRef,
          children: [(summary === null || summary === void 0 ? void 0 : summary.description) && /*#__PURE__*/_jsx(View, {
            "data-test-id": "summary-description",
            marginBlockEnd: "800",
            children: /*#__PURE__*/_jsx(Text, {
              children: summary.description
            })
          }), summary === null || summary === void 0 || (_summary$sections2 = summary.sections) === null || _summary$sections2 === void 0 ? void 0 : _summary$sections2.map(({
            title,
            bullets,
            timestamp
          }, index) => {
            const isActive = index === activeSection;
            return /*#__PURE__*/_jsx(View, {
              cursor: "pointer",
              "data-test-id": `chapter-section-${index}`,
              onClick: () => handleChapterClick(index, title, timestamp),
              onMouseEnter: () => setHoveredSection(index),
              onMouseLeave: () => setHoveredSection(-1),
              paddingBlockEnd: "200",
              ref: el => sectionRefs.current[index] = el,
              children: /*#__PURE__*/_jsxs(View, {
                backgroundColor: hoveredSection === index || isActive && hoveredSection === -1 ? 'fill-accent-blue-subtle-alt' : 'fill-transparent-default',
                borderRadius: "400",
                marginBlockEnd: "200",
                paddingBlock: "200",
                paddingInline: "400",
                children: [/*#__PURE__*/_jsx(View, {
                  alignItems: "baseline",
                  gap: "300",
                  marginBlockEnd: "400",
                  children: /*#__PURE__*/_jsx(Link, {
                    "data-test-id": `chapter-link-${index}`,
                    children: /*#__PURE__*/_jsxs(Text, {
                      as: isActive ? 'strong' : undefined,
                      color: "text-core-default",
                      children: [formatSecondsToTimestamp(timestamp), " - ", title]
                    })
                  })
                }), /*#__PURE__*/_jsx(List, {
                  marginBlockStart: "200",
                  paddingInlineStart: "800",
                  styled: true,
                  children: bullets === null || bullets === void 0 ? void 0 : bullets.map((bullet, i) => /*#__PURE__*/_jsx(ListItem, {
                    children: /*#__PURE__*/_jsx(Text, {
                      color: "text-core-default",
                      children: bullet
                    })
                  }, i))
                })]
              })
            }, timestamp);
          })]
        })
      })
    }), /*#__PURE__*/_jsx(CompanionFooter, {
      contentType: contentType,
      correlationId: correlationId,
      featureId: "SUMMARIZATION",
      primaryContentId: primaryContentId,
      trackingAction: "AI_SUMMARIZE_FEEDBACK"
    })]
  });
};
export default SummarizeTab;